import Icon from '@/components/ui/AppIcon';

interface MonetizationFeature {
  id: number;
  icon: string;
  title: string;
  description: string;
  color: string;
}

const monetizationFeatures: MonetizationFeature[] = [
  {
    id: 1,
    icon: "CurrencyRupeeIcon",
    title: "Revenue Sharing",
    description: "Earn 70% of all revenue generated from your content. Transparent, fair, and paid monthly.",
    color: "text-success"
  },
  {
    id: 2,
    icon: "UserGroupIcon",
    title: "Subscriber Model",
    description: "Build your loyal fanbase with exclusive content. Set your own subscription prices.",
    color: "text-primary"
  },
  {
    id: 3,
    icon: "GiftIcon",
    title: "Listener Tips",
    description: "Receive direct support from your audience through in-app tipping and donations.",
    color: "text-accent"
  },
  {
    id: 4,
    icon: "MegaphoneIcon",
    title: "Sponsorship Deals",
    description: "Connect with brands for sponsored content opportunities. We handle the negotiations.",
    color: "text-secondary"
  },
  {
    id: 5,
    icon: "ChartBarIcon",
    title: "Performance Bonuses",
    description: "Extra rewards for trending content, high engagement, and milestone achievements.",
    color: "text-warning"
  },
  {
    id: 6,
    icon: "BanknotesIcon",
    title: "Instant Payouts",
    description: "Fast and secure payments directly to your bank account. No minimum threshold.",
    color: "text-success"
  }
];

interface EarningTier {
  id: number;
  tier: string;
  followers: string;
  avgEarnings: string;
  color: string;
}

const earningTiers: EarningTier[] = [
  {
    id: 1,
    tier: "Beginner",
    followers: "0 - 10K",
    avgEarnings: "₹5K - ₹25K",
    color: "bg-muted"
  },
  {
    id: 2,
    tier: "Growing",
    followers: "10K - 50K",
    avgEarnings: "₹25K - ₹1L",
    color: "bg-primary/20"
  },
  {
    id: 3,
    tier: "Established",
    followers: "50K - 200K",
    avgEarnings: "₹1L - ₹4L",
    color: "bg-accent/20"
  },
  {
    id: 4,
    tier: "Top Creator",
    followers: "200K+",
    avgEarnings: "₹4L - ₹15L+",
    color: "bg-success/20"
  }
];

export default function MonetizationSection() {
  return (
    <section className="py-16 bg-gradient-to-br from-primary/5 to-accent/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-success/10 rounded-full mb-4">
            <Icon name="CurrencyRupeeIcon" size={20} className="text-success" />
            <span className="text-sm font-semibold text-success">Monetization Opportunities</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary mb-4">
            Turn Your Passion Into Profit
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Multiple revenue streams designed to help you earn while doing what you love
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {monetizationFeatures.map((feature) => (
            <div
              key={feature.id}
              className="bg-card rounded-xl p-6 shadow-warm hover:shadow-warm-lg transition-all duration-300 hover:-translate-y-1"
            >
              <div className={`w-12 h-12 ${feature.color} bg-opacity-10 rounded-lg flex items-center justify-center mb-4`}>
                <Icon name={feature.icon as any} size={24} className={feature.color} />
              </div>
              <h3 className="text-xl font-heading font-bold text-text-primary mb-2">
                {feature.title}
              </h3>
              <p className="text-text-secondary leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-card rounded-2xl p-8 shadow-warm">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-heading font-bold text-text-primary mb-2">
              Earning Potential Calculator
            </h3>
            <p className="text-text-secondary">
              See what creators at different levels are earning monthly
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {earningTiers.map((tier) => (
              <div
                key={tier.id}
                className={`${tier.color} rounded-xl p-6 text-center transition-all duration-300 hover:scale-105`}
              >
                <div className="text-sm font-semibold text-text-secondary mb-2">
                  {tier.tier}
                </div>
                <div className="text-lg font-bold text-text-primary mb-3">
                  {tier.followers}
                </div>
                <div className="text-xs text-text-secondary mb-1">
                  Avg. Monthly Earnings
                </div>
                <div className="text-2xl font-heading font-bold text-success">
                  {tier.avgEarnings}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 bg-primary/10 rounded-xl border-2 border-primary">
            <div className="flex items-start space-x-3">
              <Icon name="InformationCircleIcon" size={24} className="text-primary flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-text-primary mb-2">
                  Transparent Revenue Model
                </h4>
                <p className="text-sm text-text-secondary leading-relaxed">
                  We believe in fair compensation. You keep 70% of all revenue generated from your content, including subscriptions, tips, and ad revenue. No hidden fees, no surprises. Your success is our success.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}